import { compactNumber, rowTone, signedPct } from "../lib/format";
import type { RepeatedUnusualRow, SectorCohort, StockRow } from "../lib/types";

export function MoversTable({ rows, onSelect }: { rows: StockRow[]; onSelect?: (ticker: string) => void }) {
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Symbol</th>
          <th>Price</th>
          <th>% Chg</th>
          <th>Volume</th>
          <th>RVOL</th>
          <th>$ Volume</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 12).map((row, index) => (
          <tr key={row.ticker} onClick={() => onSelect?.(row.ticker)} className={onSelect ? "clickable-row" : undefined}>
            <td>{index + 1}</td>
            <td className="symbol">{row.ticker}</td>
            <td>{row.price?.toFixed(2) ?? "-"}</td>
            <td className={rowTone(row.changePct)}>{signedPct(row.changePct)}</td>
            <td>{compactNumber(row.volume)}</td>
            <td>{row.relativeVolume ? `${row.relativeVolume.toFixed(2)}x` : "-"}</td>
            <td>{compactNumber(row.turnover, true)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ProxyTable({ rows, onSelect }: { rows: StockRow[]; onSelect?: (ticker: string) => void }) {
  return (
    <table className="data-table proxy-table">
      <thead>
        <tr>
          <th>Symbol</th>
          <th>Price</th>
          <th>% Chg</th>
          <th>Vol</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 11).map((row) => (
          <tr key={row.ticker} onClick={() => onSelect?.(row.ticker)} className={onSelect ? "clickable-row" : undefined}>
            <td className="symbol">{row.ticker}</td>
            <td>{row.price?.toFixed(2) ?? "-"}</td>
            <td className={rowTone(row.changePct)}>{signedPct(row.changePct)}</td>
            <td>{compactNumber(row.volume)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function SectorCohortTable({
  rows,
  onSelect
}: {
  rows: SectorCohort[];
  onSelect?: (ticker: string) => void;
}) {
  if (!rows.length) {
    return <div className="empty small">No sector metadata rows were available for this payload.</div>;
  }

  return (
    <table className="data-table proxy-table">
      <thead>
        <tr>
          <th>Sector</th>
          <th>Proxy</th>
          <th>% Chg</th>
          <th>Names</th>
          <th>Vol</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 11).map((row) => (
          <tr key={row.sector} onClick={() => onSelect?.(row.proxyTicker)} className={onSelect ? "clickable-row" : undefined}>
            <td>{row.sector}</td>
            <td className="symbol">{row.proxyTicker}</td>
            <td className={rowTone(row.proxyChangePct)}>{signedPct(row.proxyChangePct)}</td>
            <td>
              {row.tickerCount} <span className={row.advancing >= row.declining ? "up" : "down"}>{row.advancing}/{row.declining}</span>
            </td>
            <td>{compactNumber(row.totalVolume)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RepeatedUnusualTable({
  rows,
  onSelect
}: {
  rows: RepeatedUnusualRow[];
  onSelect?: (ticker: string) => void;
}) {
  if (!rows.length) {
    return <div className="empty small">No repeated RVOL names match the current filters.</div>;
  }

  return (
    <table className="data-table scanner-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Symbol</th>
          <th>Hits</th>
          <th>Latest</th>
          <th>Latest RVOL</th>
          <th>Max RVOL</th>
          <th>Setup</th>
          <th>Event Path</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 12).map((row, index) => (
          <tr key={row.ticker} onClick={() => onSelect?.(row.ticker)} className={onSelect ? "clickable-row" : undefined}>
            <td>{index + 1}</td>
            <td className="symbol">{row.ticker}</td>
            <td>{row.occurrences}</td>
            <td>{row.latestDate}</td>
            <td>{row.latestRelativeVolume ? `${row.latestRelativeVolume.toFixed(2)}x` : "-"}</td>
            <td>{row.maxRelativeVolume ? `${row.maxRelativeVolume.toFixed(2)}x` : "-"}</td>
            <td className="setup-tags">
              {row.gapPct && row.gapPct > 0 ? <span title={`Gap ${signedPct(row.gapPct)}`}>Gap</span> : null}
              {row.aboveVwap ? <span title="Above VWAP">VWAP</span> : null}
              {row.nearSessionHigh ? <span title="Near session high">High</span> : null}
            </td>
            <td className="event-path">
              {row.events
                .slice(-4)
                .map((event) => `${event.date.slice(5)} ${event.relativeVolume?.toFixed(1) ?? "-"}x`)
                .join(" -> ")}
              <span>{row.currentSession ? ` now ${compactNumber(row.latestVolume)}` : ""}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
